import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// React 19 native metadata
import { Search, X } from 'lucide-react';
import { useBreadcrumbSchema } from '../common/useBreadcrumbSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return 'Recently';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
};

const BlogPage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // SEO: BreadcrumbList JSON-LD for hub indexing
  useBreadcrumbSchema('blog-index', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
  ], []));


  // Inject CollectionPage JSON-LD schema
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "iSupplyAI Blog — AI Marketing Strategy Insights",
      "description": "Expert insights on AI marketing strategy, competitive intelligence, content automation, and lead generation.",
      "url": "https://isupplyai.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "iSupplyAI",
        "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" }
      }
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'blogpage-collection-schema';
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ status: 'published', limit: '100' });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (activeCategory) params.set('category', activeCategory);

    fetch(`${API_URL}/api/blog/posts?${params}`)
      .then(r => r.json())
      .then(d => {
        const posts = (d.posts || []).map(p => ({
          slug: p.slug,
          title: p.title,
          excerpt: p.meta_description || '',
          heroImage: p.hero_image || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800',
          category: p.category || (p.tags && p.tags[0]) || 'AI Strategy',
          readTime: p.read_time || '5 min read',
          publishedAt: formatDate(p.published_at_display || p.published_at),
        }));
        setBlogPosts(posts);
        if (d.categories) setCategories(d.categories);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debouncedSearch, activeCategory]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    try {
      await fetch(`${API_URL}/api/leads/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source_post: 'blog_listing', source: 'blog_listing' })
      });
      setSubscribed(true);
    } catch { setSubscribed(true); }
    setSubLoading(false);
  };

  // Top categories (most common ones) for display
  const topCategories = useMemo(() => {
    const prioritized = ['AI Strategy', 'AI Marketing', 'AI Tools', 'Competitive Intelligence', 'Content Strategy', 'Lead Generation', 'SEO & Content'];
    return categories.filter(c => prioritized.includes(c)).slice(0, 7);
  }, [categories]);

  const featured = !debouncedSearch && !activeCategory ? blogPosts[0] : null;
  const gridPosts = featured ? blogPosts.slice(1) : blogPosts;
  const isFiltered = debouncedSearch || activeCategory;

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <>
        <title>Blog — AI Marketing Strategy Insights | iSupplyAI</title>
        <meta name="description" content="Expert insights on AI marketing strategy, competitive intelligence, content automation, and lead generation. Learn how multi-agent AI transforms business strategy." />
        <meta name="keywords" content="AI marketing blog, AI strategy insights, competitive intelligence blog, AI content marketing, marketing automation blog, multi-agent AI, ai marketing war room" />
        <link rel="canonical" href="https://isupplyai.com/blog" />
        <link rel="alternate" type="application/rss+xml" title="iSupplyAI Blog RSS" href={`${API_URL}/api/blog/feed.xml`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Blog — AI Marketing Strategy Insights | iSupplyAI" />
        <meta property="og:description" content="Expert insights on AI marketing strategy, competitive intelligence, and multi-agent AI systems." />
        <meta property="og:url" content="https://isupplyai.com/blog" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="AI Marketing Strategy Insights | iSupplyAI Blog" />
        <meta name="twitter:description" content="Expert insights on AI marketing strategy, competitive intelligence, and multi-agent AI." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
      </>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="blog-home-link">
            <img
              src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png"
              alt="iSupplyAI Logo"
              title="iSupplyAI - AI Strategy Platform"
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              iSupplyAI
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-white transition-colors"
            data-testid="blog-back-to-app"
          >
            Back to App
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="blog-listing-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <span className="text-gray-600">›</span>
          <span className="text-gray-300">Blog</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-4">
            The iSupplyAI Blog
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            AI Marketing Strategy Blog — Tips, Guides & Industry Insights
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Deep dives on AI marketing strategy, competitive intelligence, content automation, 
            and how multi-agent AI systems are transforming how businesses make decisions.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-testid="blog-search-container">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-12 pr-10 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors text-base"
              data-testid="blog-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white transition-colors"
                data-testid="blog-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category Filter Chips */}
      {topCategories.length > 0 && (
        <section className="px-4 pb-8">
          <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2" data-testid="blog-category-filters">
            <button
              onClick={() => setActiveCategory('')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                !activeCategory
                  ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/40'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'
              }`}
              data-testid="blog-filter-all"
            >
              All
            </button>
            {topCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(activeCategory === cat ? '' : cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/40'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'
                }`}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Active Filter Indicator */}
      {isFiltered && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span>{blogPosts.length} result{blogPosts.length !== 1 ? 's' : ''}</span>
            {activeCategory && (
              <button
                onClick={() => setActiveCategory('')}
                className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/15 text-purple-300 rounded-full hover:bg-purple-500/25 transition-colors"
                data-testid="blog-clear-category"
              >
                {activeCategory} <X className="w-3 h-3" />
              </button>
            )}
            {debouncedSearch && (
              <button
                onClick={() => setSearch('')}
                className="flex items-center gap-1 px-2 py-0.5 bg-white/10 text-gray-300 rounded-full hover:bg-white/15 transition-colors"
                data-testid="blog-clear-search"
              >
                "{debouncedSearch}" <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Blog Grid */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading posts...</div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-no-results">
              <p className="text-gray-500 mb-3">No articles found{debouncedSearch ? ` for "${debouncedSearch}"` : ''}</p>
              {isFiltered && (
                <button
                  onClick={() => { setSearch(''); setActiveCategory(''); }}
                  className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  className="block group mb-12"
                  data-testid="blog-featured-post"
                >
                  <article className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/40 transition-all duration-300">
                    <div className="md:flex">
                      <div className="md:w-1/2">
                        <img
                          src={featured.heroImage}
                          alt={featured.title}
                          title={featured.title}
                          className="w-full h-64 md:h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="md:w-1/2 p-8 md:p-10 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded-full">
                            {featured.category}
                          </span>
                          <span className="text-xs text-gray-500">{featured.readTime}</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors">
                          {featured.title}
                        </h2>
                        <p className="text-gray-400 mb-6 line-clamp-3">{featured.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">{featured.publishedAt}</span>
                          <span className="text-purple-400 text-sm font-semibold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                            Read article
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              )}

              {/* Post Grid */}
              <div className="grid md:grid-cols-2 gap-8">
                {gridPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <article className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/40 transition-all duration-300 h-full flex flex-col">
                      <img
                        src={post.heroImage}
                        alt={post.title}
                        title={post.title}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded-full">
                            {post.category}
                          </span>
                          <span className="text-xs text-gray-500">{post.readTime}</span>
                        </div>
                        <h2 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-gray-400 text-sm mb-4 flex-1 line-clamp-3">{post.excerpt}</p>
                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
                          <span className="text-xs text-gray-500">{post.publishedAt}</span>
                          <span className="text-purple-400 text-xs font-semibold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                            Read
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Email Capture + CTA */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-xl mx-auto">
          {!subscribed ? (
            <div className="text-center">
              <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-3">Stay ahead</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Get AI strategy insights weekly
              </h2>
              <p className="text-gray-400 mb-6">
                Join founders and marketers getting actionable AI marketing strategies. No fluff, no spam.
              </p>
              <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto mb-8" data-testid="blog-listing-capture">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="blog-listing-capture-email"
                />
                <button
                  type="submit"
                  disabled={subLoading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50"
                  data-testid="blog-listing-capture-submit"
                >
                  {subLoading ? 'Joining...' : 'Subscribe'}
                </button>
              </form>
              <Link
                to="/"
                className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                data-testid="blog-cta-button"
              >
                Or try iSupplyAI free
              </Link>
            </div>
          ) : (
            <div className="text-center" data-testid="blog-listing-capture-success">
              <p className="text-2xl font-bold text-white mb-2">You're in.</p>
              <p className="text-gray-400 mb-6">We'll send you AI strategy insights that actually move the needle.</p>
              <Link
                to="/"
                className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                data-testid="blog-cta-button"
              >
                Try iSupplyAI Free
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} iSupplyAI, LLC. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <a href={`${API_URL}/api/blog/feed.xml`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="blog-rss-link">RSS Feed</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
